'use client';

import { c as _c } from "react/compiler-runtime";
import { Drawer, Heading, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { type MouseEvent, useEffect, useId, useState } from 'react';
import { useAutoshipContentQuery } from '@libs/common-next';
import { parseRichText } from '@libs/contentful';
import { StackedDrawerBackButton } from '../stacked-drawer-boundary';
import { AutoshipInfoDrawerHeader } from './AutoshipInfoDrawerHeader';
import { isSkuMapRestricted } from './lib';
import './AutoshipInfoDrawer.scss';
type AutoshipInfoDrawerProps = {
  discountDisplay?: string;
  isFirstAutoshipEligible: boolean;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isDrawerOpen: boolean) => void;
  dataLayerPushEvent?: () => void;
  showBackButton?: boolean;
  onCloseAll?: () => void;
};
export const AutoshipInfoDrawer = props => {
  const $ = _c(48);
  const {
    isDrawerOpen,
    setIsDrawerOpen,
    showBackButton: t0,
    onCloseAll
  } = props;
  const showBackButton = t0 === undefined ? false : t0;
  const {
    autoshipContent,
    autoshipMapRestrictedContent
  } = useAutoshipContentQuery();
  const headingId = useId();
  const [hasBeenRendered, setHasBeenRendered] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHasBeenRendered(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== isDrawerOpen || $[3] !== props) {
    t3 = () => {
      if (!isDrawerOpen) {
        return;
      }
      props.dataLayerPushEvent?.();
    };
    $[2] = isDrawerOpen;
    $[3] = props;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isDrawerOpen) {
    t4 = [isDrawerOpen];
    $[5] = isDrawerOpen;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== autoshipContent || $[8] !== autoshipMapRestrictedContent || $[9] !== props.discountDisplay) {
    t5 = isSkuMapRestricted(props.discountDisplay) ? autoshipMapRestrictedContent ?? autoshipContent : autoshipContent;
    $[7] = autoshipContent;
    $[8] = autoshipMapRestrictedContent;
    $[9] = props.discountDisplay;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const selectedContent = t5;
  if (!selectedContent || !hasBeenRendered) {
    return null;
  }
  const {
    header,
    footer,
    body
  } = selectedContent;
  let t6;
  if ($[11] !== footer) {
    t6 = footer ? parseRichText(footer) : null;
    $[11] = footer;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const parsedFooter = t6;
  let t7;
  if ($[13] !== body) {
    t7 = body ? parseRichText(body) : null;
    $[13] = body;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const parsedBody = t7;
  let t8;
  if ($[15] !== setIsDrawerOpen) {
    t8 = event => {
      event.stopPropagation();
      event.preventDefault();
      setIsDrawerOpen(false);
    };
    $[15] = setIsDrawerOpen;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleBack = t8;
  let t9;
  if ($[17] !== onCloseAll || $[18] !== setIsDrawerOpen) {
    t9 = () => {
      setIsDrawerOpen(false);
      onCloseAll?.();
    };
    $[17] = onCloseAll;
    $[18] = setIsDrawerOpen;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleClose = t9;
  let t10;
  if ($[20] !== showBackButton) {
    t10 = cn("autoship-info-drawer", {
      "autoship-info-drawer--stacked": showBackButton
    });
    $[20] = showBackButton;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleBack || $[23] !== showBackButton) {
    t11 = showBackButton && <StackedDrawerBackButton className="autoship-info-drawer__back" testId="autoship-info-drawer-back-btn" onClick={handleBack} />;
    $[22] = handleBack;
    $[23] = showBackButton;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== header || $[26] !== headingId) {
    t12 = <Heading id={headingId} tagName="h2" size="headline" data-testid="drawer-title">{header}</Heading>;
    $[25] = header;
    $[26] = headingId;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <Drawer.Header>{t11}{t12}</Drawer.Header>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== props) {
    t14 = <AutoshipInfoDrawerHeader {...props} />;
    $[31] = props;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== parsedBody) {
    t15 = <div className="autoship-info-drawer__static-content">{parsedBody}</div>;
    $[33] = parsedBody;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t14 || $[36] !== t15) {
    t16 = <Drawer.Body data-testid="drawer-body">{t14}{t15}</Drawer.Body>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== parsedFooter) {
    t17 = parsedFooter && <Drawer.Footer data-testid="drawer-footer"><TextPassage>{parsedFooter}</TextPassage></Drawer.Footer>;
    $[38] = parsedFooter;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== handleClose || $[41] !== headingId || $[42] !== isDrawerOpen || $[43] !== t10 || $[44] !== t13 || $[45] !== t16 || $[46] !== t17) {
    t18 = <Drawer align="right" isActive={isDrawerOpen} onClose={handleClose} data-testid="autoship-info-drawer" className={t10} ariaLabelledBy={headingId} dismissible={true}>{t13}{t16}{t17}</Drawer>;
    $[40] = handleClose;
    $[41] = headingId;
    $[42] = isDrawerOpen;
    $[43] = t10;
    $[44] = t13;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};